import React, { useState } from "react";
import { ErrorMessage, Flex, Link, Text } from "@jsq/ds/core";
import { Form, useFormContext, CancelButton, SubmitButton } from "@jsq/ds/forms";
import { DefaultModal } from "../common/DefaultModal";
import { sharedHeaderStyle } from "../common/style_vanilla.css";
import { EntityTypeSurvey } from "../EntityTypeSurvey";
import { Question } from "../EntityTypeSurvey/types";
import { entitySurveySchema } from "./schemas";
import { EntitySurveyPageProps } from "./types";
import { areQuestionsEqual } from "./utils";

type EntitySurveyFormValues = {
  updatedQuestions: Question[];
};

const EntitySurveyForm = ({
  questions,
  entityName,
  onBack,
  isLoading,
  onFormChange,
}: {
  questions: Question[];
  entityName: string;
  onBack: (arg0: boolean) => void;
  isLoading: boolean;
  onFormChange: (hasChanged: boolean) => void;
}) => {
  const formContext = useFormContext<EntitySurveyFormValues>();

  return (
    <Flex flexDirection="column" gap="20px">
      <Text variant="regular-md">
        Additional information about {entityName} is needed in order to continue with this
        subscription.
      </Text>
      <EntityTypeSurvey
        questions={questions}
        onSurveyChange={(updatedQuestions: Question[]) => {
          void formContext.setFieldValue("updatedQuestions", updatedQuestions);
          // Check if form has changed
          const hasChanged = !areQuestionsEqual(questions, updatedQuestions);
          onFormChange(hasChanged);
        }}
      />
      <Flex flexDirection="column" gap="10px">
        {formContext.touched.updatedQuestions && formContext.errors.updatedQuestions && (
          <ErrorMessage>{formContext.errors.updatedQuestions.toString()}</ErrorMessage>
        )}
        <Flex flexDirection="row" gap="10px" justifyContent="flex-end">
          <CancelButton
            variant="neutral"
            onClick={() => {
              const hasChanged = !areQuestionsEqual(
                questions,
                formContext.values.updatedQuestions,
              );
              onBack(hasChanged);
            }}
            disabled={isLoading}
          >
            Back
          </CancelButton>
          <SubmitButton isLoading={isLoading}>Complete</SubmitButton>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const EntitySurveyPage: React.FC<EntitySurveyPageProps> = ({
  questions,
  entityName,
  isUpdating,
  onCompleted,
  onBack,
  hideBackButton,
}: EntitySurveyPageProps) => {
  const [isBackModalVisible, setIsBackModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasFormChanged, setHasFormChanged] = useState(false);

  const handleBackAction = (isChanged: boolean) => {
    if (isChanged) {
      setIsBackModalVisible(true);
    } else {
      onBack("Back button clicked");
    }
  };

  const handleBackConfirm = () => {
    setIsBackModalVisible(false);
    onBack("Back button clicked");
  };

  const handleBackCancel = () => {
    setIsBackModalVisible(false);
  };

  return (
    <Flex flexDirection="column">
      {entityName && hideBackButton != true && (
        <Link
          to=""
          variant="muted"
          icon="arrow-left"
          iconPosition="left"
          align="left"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            handleBackAction(hasFormChanged);
          }}
        >
          {entityName}
        </Link>
      )}
      <Text variant="h3" className={sharedHeaderStyle}>
        {`${entityName} Details`}
      </Text>
      <Form<EntitySurveyFormValues>
        initialValues={{
          updatedQuestions: questions.map((question) => ({ ...question, answer: "" })),
        }}
        yupValidationSchema={entitySurveySchema}
        submitOnlyChangedValues={false}
        onSubmit={async (values: EntitySurveyFormValues) => {
          setIsSubmitting(true);
          try {
            await onCompleted(values);
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        <EntitySurveyForm
          questions={questions}
          entityName={entityName}
          onBack={handleBackAction}
          isLoading={isSubmitting || isUpdating}
          onFormChange={setHasFormChanged}
        />
      </Form>
      <DefaultModal
        show={isBackModalVisible}
        title="Warning"
        body={"Are you sure you want to navigate back? Any unsaved information will be lost."}
        onClose={handleBackCancel}
        onConfirm={handleBackConfirm}
      />
    </Flex>
  );
};
